import { Inject } from '@odinjs/odin';
import moment from 'moment';

import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, BaseModalProps, ButtonAPI, DeckAPI, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { ModalType } from '@philips-internal/oswald-ui-constants';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';

import { BladderIrrigationViewerAliases, FragmentNames } from '../../../../../codegen';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../../controllers/commons/header/information-bar-header-utils';
import TimelineContext, { TimeLineEventMedication } from '../../../services/timeline-service';
import { BladderIrriationItemStatus, BladderIrrigationProps, BladderIrrigationStatus } from '../bladder-irrigation-params';
import { Endpoint } from '../../../../../constants/endpoints';
import { DialogModalConfirm } from '../../../../adep/eletronic-prescription/restart-or-start-item-config/dialogModalConfirm';
import { MedicationAndDateHeaderParams } from '../../../../../controllers/commons/header/medication-and-date-header-params';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../../utils/parameters';
import { ApplicationRoute } from '../../../../../constants/routes';
import { YesNoType } from '../../../../../utils/enum-utils';
import { ItemStatus } from '../../../../../constants/utils-adep';
import { MedicationStatus } from '../../administration-schedule/administration-schedule-types';
import { ScheduleMedicationParams }  from '../../../../schedule-medication-infusion/schedule-medication-infusion-params';
import { MedicationViewerParams } from '../../medication-viewer/medication-viewer-params';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';

@Controller({ component: BladderIrrigationViewerAliases.BLADDER_IRRIGATION_LAYOUT, fragment: FragmentNames.BLADDER_IRRIGATION_VIEWER })
export default class BladderIrrigationViewerController extends BaseController {

  @Inject(BladderIrrigationViewerAliases.PANEL_BLADDER_IRRIGATION_VIEWER_CONTENT)
  private contentPanelApi!: PanelAPI;

  @OnMount(BladderIrrigationViewerAliases.BLADDER_IRRIGATION_LAYOUT)
  handleMountLayout(): void {
    InformationBarUtils.collapsedBar = true;
    this.contentPanelApi.setVisible(false);
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.fetchBladderIrrigation().then((response) => {
      const bladderIrrigation = response as BladderIrrigationProps;

      MedicineAndSolutionsParams.params = {
        ...MedicineAndSolutionsParams.params,
        nrSequenceId: String(bladderIrrigation.irrigationSequence),
        qtDoseId: String(bladderIrrigation.itemQuantity ?? '1'),
      };
      this.context.set({ ...bladderIrrigation, encounterId, patientId });
      this.addNewParams();
    }).finally(() => {
      this.contentPanelApi.setVisible(true);
    })
  }

  private addNewParams(): void {
    const timelineItemDetail = TimelineContext.get() as TimeLineEventMedication;
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();

    MedicationViewerParams.adminSchedule = {
      dilutionDescription: '',
      prescriptionNumber: Number(timelineItemDetail.prescriptionNumber)
    };
    MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
      medicationName: timelineItemDetail.description,
      scheduleDate: timelineItemDetail.start,
      type: 'BLADDER_IRRIGATION'
    };
    ScheduleMedicationParams.prototype.scheduleSequenceNumber = Number(scheduleSequenceNumber);
  }

  @OnMount(BladderIrrigationViewerAliases.BLADDER_IRRIGATION_VIEWER_DECK)
  handleMountDeck({ target }: EventData<DeckAPI>): void {
    const timelineGastherapy = TimelineContext.get() as TimeLineEventMedication;
    const { descriptionTopography, side } = this.context.getBulkData() as unknown as BladderIrrigationProps;
    target.setData([{
      description: timelineGastherapy.description,
      scheduleDate: timelineGastherapy.start,
      side,
      statusItem: timelineGastherapy.status,
      topography: descriptionTopography
    }]);
  }

  @OnMount(BladderIrrigationViewerAliases.RESCHEDULE_ACTION)
  handleMountRescheduleAction({ target }: EventData<ActionAPI>): void {
    target.setEnabled(this.isAllowedToReschedule());
  }

  @OnMount(BladderIrrigationViewerAliases.REVERSE_ACTION)
  handleMountActionReverse({ target }: EventData<ActionAPI>): void {
    target.setEnabled(this.isAllowedToReverse())
  }

  @OnClick(BladderIrrigationViewerAliases.REVERSE_ACTION)
  handleClickActionReverse(): void {
    this.openReverseModal();
  }

  private isAllowedToReverse(): boolean {
    const allowRevertParam = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_REVERSING_TIME_MANAGEMENT)
    if (allowRevertParam !== YesNoType.YES) {
      return false
    }
    const { itemStatus, status } = TimelineContext.get();
    const notIsPendingStatus = itemStatus !== BladderIrriationItemStatus.PENDING;
    const notIsPendingItemStatus = status !== BladderIrrigationStatus.PENDING
    return notIsPendingStatus && notIsPendingItemStatus;
  }

  private openReverseModal(): void {
    const scheduleTitleDescription = MessageService.translate('text.revert-last-event');
    const bladderIrrigation = this.context.getBulkData();
    const dialogModal: BaseModalProps = {
      contextData: { bladderIrrigation },
      focusOnOpen: false,
      fragment: FragmentNames.BLADDER_IRRIGATION_REVERSE,
      id: FragmentNames.BLADDER_IRRIGATION_REVERSE,
      onBeforeClose: (event) => DialogModalConfirm(event, FragmentNames.BLADDER_IRRIGATION_REVERSE),
      title: scheduleTitleDescription,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  @OnMount(BladderIrrigationViewerAliases.END_BLADDER_IRRIGATION)
  handleOnMountEndBladderIrrigation({ target }: EventData<ButtonAPI>): void {
    const { itemStatus, status } = TimelineContext.get();
    const isPendingOrFinishedItemStatus = [String(BladderIrriationItemStatus.PENDING), BladderIrriationItemStatus.FINISHED].includes(itemStatus);
    const isStartedAndPendingStatus = itemStatus === BladderIrriationItemStatus.STARTED && status === BladderIrrigationStatus.PENDING;
    const isPendingOrAdministeredStatus = status === BladderIrrigationStatus.PENDING || status === BladderIrrigationStatus.ADMINISTERED;

    const buttonEndIsVisible = (isPendingOrFinishedItemStatus || isStartedAndPendingStatus) && isPendingOrAdministeredStatus;
    target.setVisible(!buttonEndIsVisible);
  }

  @OnMount(BladderIrrigationViewerAliases.START_BLADDER_IRRIGATION)
  handleOnMountStarBladderIrrigation(event: MountEvent): void {
    const startButton = event.target as ButtonAPI;
    const { itemStatus, status } = TimelineContext.get();
    const isPendingOrStartedStatus = [String(BladderIrriationItemStatus.PENDING), BladderIrriationItemStatus.STARTED].includes(itemStatus)
    const buttonStartIsVisible = isPendingOrStartedStatus && status === BladderIrrigationStatus.PENDING;

    startButton.setVisible(buttonStartIsVisible);
  }

  @OnClick(BladderIrrigationViewerAliases.END_BLADDER_IRRIGATION)
  handleOnClickEndBladderIrrigation(): void {
    const contextItem = this.context.getBulkData();
    const dialogModal: BaseModalProps = {
      contextData: { contextItem },
      focusOnOpen: false,
      fragment: FragmentNames.BLADDER_IRRIGATION_TERMINATE,
      id: FragmentNames.BLADDER_IRRIGATION_TERMINATE,
      onBeforeClose: (event) => DialogModalConfirm(event, FragmentNames.BLADDER_IRRIGATION_TERMINATE),
      title: 'text.emar-record',
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  @OnClick(BladderIrrigationViewerAliases.RESCHEDULE_ACTION)
  onClickRescheduleAction(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const route = ApplicationRoute.EMAR_SCHEDULE_MEDICATION_INFUSION;
    applicationContext.redirect(route, { encounterId, patientId });
  }

  private isAllowedToReschedule(): boolean {
    const { prescriptionValidate } = this.context.getBulkData() as unknown as BladderIrrigationProps;
    const isRescheduleAllowed = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_RESCHEDULE_MEDICATION) === YesNoType.YES;
    const prescriptionValidateDate = moment(prescriptionValidate, 'DD/MM/YYYY hh:mm:ss');
    const today = moment();
    const { status } = TimelineContext.get();
    const isStatusPending = [ItemStatus.PENDING, MedicationStatus.PENDING as string].includes(status);
    const isBeforeValidateDate = today.isBefore(prescriptionValidateDate);

    return isRescheduleAllowed && isStatusPending && isBeforeValidateDate;
  }

  private fetchBladderIrrigation(): Promise<BladderIrrigationProps> {
    const { cpoeSequence, prescriptionNumber } = TimelineContext.get();
    return HttpService.get<BladderIrrigationProps>(Endpoint.EMAR_GET_BLARRED_IRRIGATION, {
      bladderIrrigationId: cpoeSequence,
      prescriptionId: prescriptionNumber
    }).then((response) => {
      const responseBody = response.body as BladderIrrigationProps;
      this.context.set({ ...responseBody });
      return responseBody;
    })
  }

  @OnClick(BladderIrrigationViewerAliases.START_BLADDER_IRRIGATION)
  handleClickExecuteButton(): void {
    ModalService.openDefault({
      contextData: this.context.getBulkData() as Record<string, Value>,
      fragment: FragmentNames.BLADDER_IRRIGATION_START,
      id: FragmentNames.BLADDER_IRRIGATION_START,
      title: 'text.emar-record',
    })
  }
}

